import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Divider } from "@mui/material";
import { Avatar } from "Components";

import AIChatList from "Modules/chat/views/chatTypes/ai";
import SupportChatList from "Modules/chat/views/chatTypes/support";
import { openChat, closeChat } from "Modules/chat/views/chatTypes/support/redux/supportChannelsSlice";
import { selectAIUnreadCount, fetchAIUnreadCounts, incrementUnreadCount } from "Modules/chat/views/chatTypes/ai/redux/aiChatListSlice";

import ChatsListItem from "./components/ChatsListItem";
import ChatsListSkeleton from "./components/ChatsListSkeleton";
import { useFeatureEnabled } from 'Components/FeatureToggle';

import "Modules/chat/index.scss";

const ChatsList = ({ authId, chatUsers, locale, messages, children }) => {
  console.log('Deploy suscess with v4');
  const dispatch = useDispatch();
  const { chats, openModal, activeChatType, loading } = useSelector(
    (state) => state.supportChatListRedux
  );
  
  // Get AI unread count and streaming state from Redux
  const aiUnreadCount = useSelector(selectAIUnreadCount);
  const isStreaming = useSelector((state) => state.aiChatListRedux.isStreaming);

  // Separate refs so each effect has its own previous-value tracking
  const prevActiveChatTypeRef = useRef(activeChatType);  // for activeChatType effect
  const prevIsStreamingRef = useRef(isStreaming);         // for isStreaming effect
  // Flag: user backed out while agent was still streaming → watch for COMPLETE
  const streamingWhileAwayRef = useRef(false);

  /**
   * Detect activeChatType transition: 'ai' → null
   *   • If still streaming when backing out → set flag, wait for COMPLETE
   *   • If already done streaming when backing out (Case B) → sync with backend
   */
  useEffect(() => {
    const prev = prevActiveChatTypeRef.current;
    prevActiveChatTypeRef.current = activeChatType;

    const closedAIChat = prev === 'ai' && activeChatType !== 'ai';
    if (!closedAIChat) return;

    if (isStreaming) {
      // Agent still typing → flag it, COMPLETE will fire later and increment count
      streamingWhileAwayRef.current = true;
      console.log('🔔 [ChatsList] Backed out mid-stream, will count when COMPLETE fires');
    } else {
      // Agent already finished → sync backend to get latest count
      console.log('🔔 [ChatsList] Closed AI chat after COMPLETE → syncing unread count');
      if (authId) dispatch(fetchAIUnreadCounts({ userId: authId }));
    }
  }, [activeChatType, isStreaming, authId, dispatch]);

  /**
   * Detect isStreaming transition: true → false (COMPLETE/ERROR event received)
   * Only act when user is NOT in AI chat (away):
   *   • Increment count locally immediately (works even if backend not ready)
   *   • Also try to sync with backend
   */
  useEffect(() => {
    const prevStreaming = prevIsStreamingRef.current;
    prevIsStreamingRef.current = isStreaming;

    const justFinished = prevStreaming === true && isStreaming === false;
    const userIsAway = activeChatType !== 'ai';

    if (justFinished && userIsAway) {
      console.log('🔔 [ChatsList] COMPLETE fired while away → incrementing unread count');
      dispatch(incrementUnreadCount(1));
      streamingWhileAwayRef.current = false;
      if (authId) dispatch(fetchAIUnreadCounts({ userId: authId }));
    }
  }, [isStreaming, activeChatType, authId, dispatch]);

  const handleOpen = (chatId) => {
    dispatch(openChat({ chatId }));
  };

  const handleClose = () => {
    dispatch(closeChat());
  };

  const renderSpecialItems = () => {
    let renderChats = chats;
    const enableNewSupportChat = useFeatureEnabled('support-chat');

    if (!enableNewSupportChat) {
      renderChats = renderChats.filter(chat => chat.id !== "support");
    }

    const enableAIChat = useFeatureEnabled('neurion-ai-chat');

    if (!enableAIChat) {
      renderChats = renderChats.filter(chat => chat.id !== "ai");
    }
    if (renderChats.length) {
      const listItems = renderChats.map((chat) => {
        // Use AI unread count for AI chat item, otherwise use lastMessage unreadCount
        const isAIChat = chat.id === "ai";
        const unreadCount = isAIChat ? aiUnreadCount : chat.lastMessage?.unreadCount;
        
        return (
          <ChatsListItem
            key={chat.id}
            variant={chat.type || (isAIChat ? "ai" : "support")}
            leading={<Avatar value={{ url: chat.iconUrl }} width={48} />}
            title={messages[`${chat.type}_title`]}
            subtitle={chat.lastMessage?.message || messages[`${chat.type}_defaultMessage`]}
            unreadCount={unreadCount}
            onClick={() => handleOpen(chat.id)}
            disableChevron
          />
        );
      });
      return (
        <>
          <div className="chat-list-featured">
            {loading ? <ChatsListSkeleton length={renderChats.length || 2} /> : listItems}
          </div>
          <Divider />
        </>
      );
    } else {
      return <></>;
    }
  };

  return (
    <div className="chat-list">
      {renderSpecialItems()}
      {activeChatType === "ai" && (
        <AIChatList openModal={openModal} onClose={handleClose} />
      )}
      {activeChatType === "support" && (
        <SupportChatList
          authId={authId}
          chatUsers={chatUsers}
          locale={locale}
          messages={messages}
          onClose={handleClose}
          routingAttributes={{
            'Routing_Source__c': 'Web_Application',
            'Routing_User_ID__c': authId,
            'Routing_Locale__c': locale
          }}
        />
      )}
      <div className="chat-list-section">
        {children}
      </div>
    </div>
  );
};

export default ChatsList;
